import React from 'react';
import { useTranslation } from 'react-i18next';
import { SiteArea } from 'graphql-common';
import { GraphQLFormattedError } from 'graphql';
import { Values } from '@lib/interfaces/form';
import { GetLoadOptions } from '@lib/components/Select/types';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import Modal from '@lib/components/Modal/Modal';
import OfflineLabel from '@lib/components/OfflineLabel/OfflineLabel';
import AreaEditForm from '../AreaEditForm/AreaEditForm';
import AreaView from '../AreaView/AreaView';
import styles from './SiteAreaViewEditModal.module.scss';

interface SiteAreaViewEditModalProps {
  areaData?: SiteArea;
  formRef?: React.RefObject<FormRef | undefined>;
  // `getDocumentationsLoadOptions`/`openDocumentsModal` are only supplied by
  // flows that expose the documents field (SiteAreasContainer). Other callers
  // (e.g. DocumentView AreasTab) omit them to hide the field.
  getDocumentationsLoadOptions?: GetLoadOptions;
  graphQLErrors?: readonly GraphQLFormattedError[];
  isDocumentationLocked?: boolean;
  isEditMode: boolean;
  isOpen: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (values: Values) => void;
  onToggleMode: () => void;
  openDocumentsModal?: () => void;
}

export default function SiteAreaViewEditModal({
  areaData,
  formRef,
  getDocumentationsLoadOptions,
  graphQLErrors,
  isDocumentationLocked,
  isEditMode,
  isOpen,
  loading,
  onClose,
  onSubmit,
  onToggleMode,
  openDocumentsModal,
}: SiteAreaViewEditModalProps) {
  const { t } = useTranslation();
  const areaName = areaData?.name || '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('edit-site-area') : areaName}
      titleAlign="left"
      modalContentClassName={
        !isEditMode ? styles.modalContentClassName : undefined
      }
      descriptionChildren={
        !isEditMode && areaData?.poorWifi ? (
          <div className={styles.offlineLabel}>
            <OfflineLabel />
          </div>
        ) : undefined
      }
    >
      <div>
        {isEditMode && (
          <AreaEditForm
            areaData={areaData as Values}
            formRef={formRef}
            getDocumentationsLoadOptions={getDocumentationsLoadOptions}
            graphQLErrors={graphQLErrors}
            loading={loading}
            onCancel={onClose}
            onSubmit={onSubmit}
            openDocumentsModal={openDocumentsModal}
          />
        )}
        {!isEditMode && (
          <AreaView
            areaData={areaData}
            isDocumentationLocked={isDocumentationLocked}
            onEdit={onToggleMode}
          />
        )}
      </div>
    </Modal>
  );
}
